/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @next/next/no-img-element */
// libraries
import Link from 'next/link';
import { capitalizeWords, ensureReadableTextColor, isColorLight, montaLinks } from '../../../utils/FunctionsGlobals';
import { appendCodsiteQuery } from '../../../utils/url';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { fontesMaioresDoSite } from '../../../utils/fontesMaioresPorSite';
// utils
import styles from './Popular.module.css';

/**
 * PESQUISAS MAIS POPULARES — variantes de cor (tbl_sites.modelo_pesquisa_popular).
 *
 * A coluna era só liga/desliga (0/1) e o bloco ficava PRESO ao `cor5` ("Fundo geral"
 * do site inteiro): quem queria mudar a cor daqui repintava a home toda. Agora ela
 * também escolhe a paleta, e o Builder expõe isso como a seção `populares`.
 *
 *   0 = não mostrar
 *   1 = fundo do site   (COMO SEMPRE FOI — fórmula preservada, 2.2k tenants dependem)
 *   2 = faixa do rodapé (cor3 + cor7, o par dedicado do rodapé)
 *   3 = destaque da marca (cor2)
 */
type PaletaPopular = { bg?: string; fg?: string };

// cs2659 (12/10): "Pesquisas mais populares" com fundo BRANCO e texto PRETO, independente da variante
// (o site é escuro: cor5 #101014). Só nesta licença; fora dela a paleta segue a variante.
const POPULARES_FUNDO_BRANCO_TEXTO_PRETO = new Set([2659]);

function paletaPopulares(site: any, variante: number): PaletaPopular {
    if (POPULARES_FUNDO_BRANCO_TEXTO_PRETO.has(Number(site?.id))) return { bg: '#ffffff', fg: '#000000' };
    switch (variante) {
        case 2: {
            // `cor7` é a cor DEDICADA de texto do rodapé; se o tenant escolheu uma que
            // não contrasta com a faixa, cai no automático (mesma ideia do MegaFooter).
            const bg = site?.cor3 || '#222222';
            return { bg, fg: ensureReadableTextColor(site?.cor7, bg) };
        }
        case 3: {
            const bg = site?.cor2 || '#222222';
            return { bg, fg: ensureReadableTextColor(isColorLight(bg) ? site?.cor11 : site?.cor13, bg) };
        }
        default: {
            // NÃO reescrever: é byte-a-byte o comportamento publicado hoje.
            const bg = site?.cor5;
            return { bg, fg: isColorLight(bg) ? site?.cor11 : site?.cor13 };
        }
    }
}

const Popular: React.FC<{
    site: any;
    allRodape: any;
    codsite?: any;
    origin?: any;
}> = ({ site, allRodape, codsite, origin }) => {
    // Gate único do bloco (antes estava repetido nos 3 rodapés que o renderizavam).
    const variante = Number(site?.modelo_pesquisa_popular ?? 1);
    // cs2551 pediu o bloco fora do ar em TODAS as páginas — nas internas o `hidePopular`
    // não chega, então a exceção mora aqui. Sai quando a coluna do 2551 virar 0.
    if (!variante || Number(site?.id) === 2551) return null;

    const { bg, fg } = paletaPopulares(site, variante);
    const withCodsite = (href: string) => appendCodsiteQuery(href, codsite, origin);

    // useState
    const allRodape1 = allRodape;
    const allLinks = allRodape1?.length > 0 ? montaLinks(allRodape1) : []

    return (
        <>
            {allLinks?.length > 0 ? (
                // BUILDER: âncora estável da seção "populares" — é ela que deixa o picker
                // do "Ajustar com IA" selecionar o bloco e o goto-section rolar até aqui.
                <div
                    className={fontesMaioresDoSite(site) ? `${styles.mais_populares} ${styles.fontesMaiores}` : styles.mais_populares}
                    data-builder-section="populares"
                    style={{ background: bg, ['--pop-bg' as any]: bg ?? '', ['--pop-fg' as any]: fg ?? '' } as React.CSSProperties}
                >
                    <div className={styles.pequisa_popular} style={{color: fg}}>{site?.id == 2456 ? "PESQUISAS RECENTES" : site?.id == 1630 ? "PESQUISAS FREQUENTES" : site?.id == 2098 ? "PESQUISAS MAIS ACESSADAS" : "PESQUISAS MAIS POPULARES"}</div>
                    <ul className={styles.mais_buscados}>
                        {(allLinks?.length > 0 ? allLinks : [])
                            ?.slice(0, 17)
                            ?.map((v: any, cont: number) => (
                                <li className={styles.buscados} key={cont} style={{color: fg, border: `1px solid ${fg}`}}>
                                    <Link prefetch={false} href={withCodsite(v?.url)} passHref onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: v?.title, content_category: 'popular_search' })}>
                                        <div
                                            title={v?.title}
                                        >
                                            {capitalizeWords(v?.title.toLowerCase()).replaceAll("Em ","em ")}
                                        </div>
                                    </Link>
                                </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </>
    );
};

export default Popular;
